import { useEffect } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { useLocation } from 'react-router-dom';

/**
 * hook to track Time to Interactive (TTI) using Datadog RUM
 * observes the First Contentful Paint (FCP) and Long Tasks to determine TTIh
 */
const useTTI = () => {
  const location = useLocation();

  useEffect(() => {
    if (!datadogRum.getInitConfiguration()) return;

    const routeStartTime = performance.now();
    let fcpTime: number | null = null;
    let lastLongTaskEnd = 0;
    let isTTIRecorded = false; //prevent multiple TTI records
    let timerId: NodeJS.Timeout;

    //get FCP time
    const fcpObserver = new PerformanceObserver((entryList) => {
      const entries = entryList.getEntriesByName('first-contentful-paint');
      if (entries.length > 0) {
        fcpTime = entries[0].startTime;
      }
    });

    //get long task time
    const longTaskObserver = new PerformanceObserver((list) => {
      list.getEntries().forEach((entry) => {
        if (fcpTime && entry.startTime > fcpTime) {
          lastLongTaskEnd = Math.max(lastLongTaskEnd, entry.startTime + entry.duration);
        }
      });
    });

    fcpObserver.observe({ type: 'paint', buffered: true }); //observe FCP
    longTaskObserver.observe({ entryTypes: ['longtask'] }); //observe long tasks

    const checkInterval = 500;
    const maxWaitTime = 30000; // 30 seconds
    const startCheck = performance.now();

    //send TTI to Datadog after 5 seconds of inactivity
    const sendTTI = () => {
      try {
        const now = performance.now();
        if ((now - lastLongTaskEnd >= 5000 && fcpTime !== null) || now - startCheck >= maxWaitTime) {
          const rawTTI = lastLongTaskEnd || now;
          const ttiValue = Math.round(rawTTI - routeStartTime);

          if (!isTTIRecorded) {
            datadogRum.addAction('custom_tti', { tti: ttiValue });
            isTTIRecorded = true;
          }

          //clean up after TTI is recorded
          fcpObserver.disconnect();
          longTaskObserver.disconnect();
          if (timerId) clearTimeout(timerId);
        } else {
          //always clear any pending timer
          if (timerId) clearTimeout(timerId);
          timerId = setTimeout(sendTTI, checkInterval);
        }
      } catch (err) {
        console.log('Use TTI hook error');
      }
    };

    sendTTI();

    return () => {
      try {
        //cleanup observers and timer
        fcpObserver.disconnect();
        longTaskObserver.disconnect();
        if (timerId) clearTimeout(timerId);
      } catch (err) {
        console.log('Use TTI hook cleanup error');
      }
    };
  }, [location.pathname]);
};

export default useTTI;
